import type {
  HeroButtonVariant,
  HeroLayout,
  HeroProps,
  HeroSize,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, SelectField, TextField } from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const LAYOUT = ["image-right", "image-left", "centered", "background"] as const;
const SIZE = ["sm", "md", "lg"] as const;
const VARIANT = ["primary", "secondary", "ghost"] as const;

export function HeroInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as HeroProps;

  return (
    <>
      <SelectField
        label="Layout"
        value={p.layout ?? "image-right"}
        options={LAYOUT}
        onChange={(v) => update(widget.id, { layout: v as HeroLayout })}
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as HeroSize })}
      />
      <TextField
        label="Eyebrow (optional)"
        value={p.eyebrow ?? ""}
        onChange={(v) => update(widget.id, { eyebrow: v || undefined })}
      />
      <TextField
        label="Title"
        value={p.title}
        onChange={(v) => update(widget.id, { title: v })}
      />
      <TextField
        label="Subtitle"
        value={p.subtitle ?? ""}
        multiline
        onChange={(v) => update(widget.id, { subtitle: v || undefined })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Primary CTA
      </div>
      <TextField
        label="Label"
        value={p.primaryCtaLabel ?? ""}
        onChange={(v) => update(widget.id, { primaryCtaLabel: v || undefined })}
      />
      <TextField
        label="URL"
        value={p.primaryCtaHref ?? ""}
        onChange={(v) => update(widget.id, { primaryCtaHref: v || undefined })}
      />
      <SelectField
        label="Style"
        value={p.primaryCtaVariant ?? "primary"}
        options={VARIANT}
        onChange={(v) => update(widget.id, { primaryCtaVariant: v as HeroButtonVariant })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Secondary CTA
      </div>
      <TextField
        label="Label"
        value={p.secondaryCtaLabel ?? ""}
        onChange={(v) => update(widget.id, { secondaryCtaLabel: v || undefined })}
      />
      <TextField
        label="URL"
        value={p.secondaryCtaHref ?? ""}
        onChange={(v) => update(widget.id, { secondaryCtaHref: v || undefined })}
      />
      <SelectField
        label="Style"
        value={p.secondaryCtaVariant ?? "ghost"}
        options={VARIANT}
        onChange={(v) =>
          update(widget.id, { secondaryCtaVariant: v as HeroButtonVariant })
        }
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Image
      </div>
      <MediaField
        label="Image URL"
        value={p.imageUrl ?? ""}
        onChange={(v) => update(widget.id, { imageUrl: v || undefined })}
      />
      <TextField
        label="Image alt"
        value={p.imageAlt ?? ""}
        onChange={(v) => update(widget.id, { imageAlt: v || undefined })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Colours
      </div>
      <ColorField
        label="Background"
        value={p.background ?? "#ffffff"}
        onChange={(v) => update(widget.id, { background: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Accent"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />
    </>
  );
}
